import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';
import { LocalizedComponentProps, LocalizedText, withLocalization } from 'bernie-l10n';

import { FindAnswersFormLaunchLocation } from 'stores/find-answers-store';

import { QuestionsViewModel, ReviewsViewModel } from 'src/common/models';

import { classNames } from 'uitk-react-utils';
import { UitkErrorSummary } from 'uitk-react-error-summary';
import { UitkSecondaryButton } from 'uitk-react-button';
import { UitkTab, UitkTabs } from 'uitk-react-tabs';

import { ResultsCard } from 'utils/infosite-search-utils';

import { FindAnswersResultType } from 'components/infosite/find-answers/find-answers-form';
import { PropertyDetailsResult } from 'components/infosite/find-answers/property-details-result';
import { QuestionAnswer } from 'components/infosite/find-answers/question-answer';
import { Review } from 'components/infosite/property-details/review';

interface FindAnswersResultState {
  currentTab: string;
}

export interface FindAnswersResultProps extends LocalizedComponentProps {
  propertyDetails: ResultsCard[];
  reviews: ReviewsViewModel;
  questionAnswers: QuestionsViewModel;
  propertyDetailsStartIndex: number;
  searchString: string;
  handleMorePropertyDetails: any;
  showReviewTab?: boolean;
  errorFetching?: boolean;
  handleMoreReviews: () => void;
  handleMoreQuestions: () => void;
  formLaunchLocation?: FindAnswersFormLaunchLocation;
  analytics?: AnalyticsStore;
  enabledForConventionalLodging?: boolean;
}

@withLocalization
@inject('analytics')
@observer
export class FindAnswersResult extends React.Component<FindAnswersResultProps, FindAnswersResultState> {
  constructor(props) {
    super(props);
    this.state = {
      currentTab:
        this.props.formLaunchLocation && this.props.formLaunchLocation === FindAnswersFormLaunchLocation.REVIEWS_DIALOG
          ? this.props.l10n.formatText('hotels.infosite.findAnswers.form.tab.reviews')
          : this.props.l10n.formatText('hotels.infosite.findAnswers.form.tab.propertyDetails')
    };

    this.handleTabClick = this.handleTabClick.bind(this);
    this.initializeOmnitureTrackingForResultTabs = this.initializeOmnitureTrackingForResultTabs.bind(this);
  }

  private getResultMessaging(findAnswersResultType: FindAnswersResultType, resultCount: number): string {
    switch (findAnswersResultType) {
      case FindAnswersResultType.PROPERTY_DETAILS:
        return resultCount === 1
          ? 'hotels.infosite.findAnswers.form.search.resultsMessage.singular.propertyDetails'
          : 'hotels.infosite.findAnswers.form.search.resultsMessage.propertyDetails';
      case FindAnswersResultType.REVIEWS:
        return resultCount === 1
          ? 'hotels.infosite.findAnswers.form.search.resultsMessage.singular.reviews'
          : 'hotels.infosite.findAnswers.form.search.resultsMessage.reviews';
      case FindAnswersResultType.QUESTIONS:
        return resultCount === 1
          ? 'hotels.infosite.findAnswers.form.search.resultsMessage.singular.travelerQnA'
          : 'hotels.infosite.findAnswers.form.search.resultsMessage.travelerQnA';
      default:
        return 'hotels.infosite.findAnswers.form.search.resultsMessage.singular.propertyDetails';
    }
  }

  private showMoreReviewsButton(reviews: ReviewsViewModel): boolean {
    return reviews && reviews.userReviews && reviews.userReviews.length < reviews.count;
  }

  private showMoreQuestionsButton(questions: QuestionsViewModel): boolean {
    return questions && questions.userQuestions && questions.userQuestions.length < questions.count;
  }

  private initializeOmnitureTrackingForResultTabs(findAnswersResultType: string): void {
    let opts = '';
    const { l10n, analytics } = this.props;
    const propertyDetailsTabName = l10n.formatText('hotels.infosite.findAnswers.form.tab.propertyDetails');
    const reviewsTabName = l10n.formatText('hotels.infosite.findAnswers.form.tab.reviews');
    const questionsTabName = l10n.formatText('hotels.infosite.findAnswers.form.tab.travelerQnA');

    switch (findAnswersResultType) {
      case propertyDetailsTabName:
        opts = '.FindAnswers.Tab.PROPERTYDETAILS';
        break;
      case reviewsTabName:
        opts = '.FindAnswers.Tab.REVIEWS';
        break;
      case questionsTabName:
        opts = '.FindAnswers.Tab.QUESTIONS';
        break;
      default:
    }

    analytics.clickTracker(opts, 'Change tab on click', '', '', '')();
  }

  private handleTabClick(selectedTab): void {
    this.setState({
      currentTab: selectedTab
    });
    this.initializeOmnitureTrackingForResultTabs(selectedTab);
  }

  public render() {
    const { currentTab } = this.state;
    const {
      errorFetching,
      l10n,
      propertyDetails,
      handleMorePropertyDetails,
      handleMoreReviews,
      handleMoreQuestions,
      propertyDetailsStartIndex,
      searchString,
      showReviewTab,
      formLaunchLocation,
      questionAnswers,
      reviews,
      enabledForConventionalLodging
    } = this.props;

    const propertyDetailsResult =
      (propertyDetails && propertyDetails.length) > 0 ? (
        <React.Fragment>
          {propertyDetails.filter((card, index) => index < propertyDetailsStartIndex).map((card, index) => (
            <PropertyDetailsResult card={card} key={index} searchString={searchString} index={index} />
          ))}
        </React.Fragment>
      ) : null;

    const userReviewsModule = (reviews && reviews.userReviews && reviews.userReviews.length) > 0 && (
      <section className="review-result-cards">
        {reviews.userReviews.map(
          (review, index) =>
            review && (
              <section className="uitk-card-separator-bottom">
                <Review review={review} key={index} searchText={searchString} />
              </section>
            )
        )}
      </section>
    );

    const questionAnswerModule = (questionAnswers && questionAnswers.userQuestions && questionAnswers.userQuestions.length) > 0 && (
      <section className="review-cards">
        {questionAnswers.userQuestions.map(
          (question, index) => question && <QuestionAnswer question={question} index={index} key={index} searchString={searchString} />
        )}
      </section>
    );

    const propertyDetailsResultCount = propertyDetails ? propertyDetails.length : 0;
    const propertyDetailsResultMessaging = this.getResultMessaging(FindAnswersResultType.PROPERTY_DETAILS, propertyDetailsResultCount);
    const propertyDetailsClass = classNames({
      'is-hidden':
        formLaunchLocation && formLaunchLocation === FindAnswersFormLaunchLocation.REVIEWS_DIALOG && propertyDetailsResultCount === 0
    });

    const reviewsResultCount = reviews && reviews.count > 0 ? reviews.count : 0;
    const reviewsResultCountMessaging = this.getResultMessaging(FindAnswersResultType.REVIEWS, reviewsResultCount);
    const reviewsClass = classNames({ 'is-hidden': !showReviewTab || !enabledForConventionalLodging });

    const questionsResultCount = questionAnswers && questionAnswers.count > 0 ? questionAnswers.count : 0;
    const questionsResultMessaging = this.getResultMessaging(FindAnswersResultType.QUESTIONS, questionsResultCount);
    const questionsClass = classNames({ 'is-hidden': !enabledForConventionalLodging });

    return (
      <React.Fragment>
        <UitkTabs selectedTab={currentTab} onTabSelect={this.handleTabClick}>
          <UitkTab
            id="propertyDetailsTab"
            name={l10n.formatText('hotels.infosite.findAnswers.form.tab.propertyDetails')}
            className={propertyDetailsClass}
          >
            <SearchResultsCount message={propertyDetailsResultMessaging} data={[propertyDetailsResultCount]} />
            {propertyDetailsResult}
            {propertyDetails &&
              propertyDetails.length > propertyDetailsStartIndex && <ShowMoreButton onClickHandler={handleMorePropertyDetails} />}
          </UitkTab>
          <UitkTab id="reviewsTab" name={l10n.formatText('hotels.infosite.findAnswers.form.tab.reviews')} className={reviewsClass}>
            {!errorFetching && (
              <React.Fragment>
                <SearchResultsCount message={reviewsResultCountMessaging} data={[reviewsResultCount]} />
                {userReviewsModule}
                {this.showMoreReviewsButton(reviews) && <ShowMoreButton onClickHandler={handleMoreReviews} />}
              </React.Fragment>
            )}
            {errorFetching && <SearchErrorSummary l10n={l10n} linkClickFocusId="reviewsTab" />}
          </UitkTab>
          <UitkTab id="questionsTab" name={l10n.formatText('hotels.infosite.findAnswers.form.tab.travelerQnA')} className={questionsClass}>
            {!errorFetching && (
              <React.Fragment>
                <SearchResultsCount message={questionsResultMessaging} data={[questionsResultCount]} />
                {questionAnswerModule}
                {this.showMoreQuestionsButton(questionAnswers) && <ShowMoreButton onClickHandler={handleMoreQuestions} />}
              </React.Fragment>
            )}
            {errorFetching && <SearchErrorSummary l10n={l10n} linkClickFocusId="questionsTab" />}
          </UitkTab>
        </UitkTabs>
      </React.Fragment>
    );
  }
}

interface SearchResultsCountProps {
  message: string;
  data: any;
}

const SearchResultsCount: React.FunctionComponent<SearchResultsCountProps> = ({ message, data }) => (
  <div className="uitk-grid all-grid-align-center all-y-padding-two pwa-theme--grey-700">
    <LocalizedText message={message} data={data} />
  </div>
);
SearchResultsCount.displayName = 'SearchResultsCount';

interface SearchErrorSummaryProps extends LocalizedComponentProps {
  linkClickFocusId: string;
}

const SearchErrorSummary: React.FunctionComponent<SearchErrorSummaryProps> = ({ linkClickFocusId, l10n }) => (
  <UitkErrorSummary
    heading={l10n.formatText('user.errorSummary.default.heading')}
    link={l10n.formatText('hotels.infosite.findAnswers.form.search.errorSummary')}
    linkClickFocus={{}}
    linkClickFocusId={linkClickFocusId}
  />
);
SearchErrorSummary.displayName = 'SearchErrorSummary';

interface ShowMoreButtonProps {
  onClickHandler: () => void;
}

const ShowMoreButton: React.FunctionComponent<ShowMoreButtonProps> = ({ onClickHandler }) => (
  <section className="more-reviews">
    <UitkSecondaryButton
      className="more-reviews-button"
      onClick={onClickHandler}
      text={<LocalizedText message="hotels.infosite.findAnswers.searchResults.paginationButton.label" />}
    />
  </section>
);
ShowMoreButton.displayName = 'ShowMoreButton';
